/**
 * Tiempos de espera de un proveedor: la cuenta, en un solo lugar.
 *
 * Es pura a propósito —sin base, sin reloj— igual que `engine.ts`, así se puede probar con una
 * calculadora y la usan por igual el servidor, la importación y la pantalla. Si la pantalla
 * calculara el suyo por su lado, el número que muestra el slider y el que guarda la base
 * podrían discrepar, y nadie se enteraría hasta que una compra saliera mal.
 */

/** Un tramo del recorrido, como rango. Con un dato firme, `min === max`. */
export interface Tramo {
  min: number;
  max: number;
}

export interface TiemposProveedor {
  fabricacion: Tramo;
  transito: Tramo;
  nacionalizacion: Tramo;
  /** Dónde pararse dentro del rango: 0 = el mínimo, 50 = el promedio, 100 = el máximo. */
  puntoRango: number;
}

/** El rango total del proveedor: la suma de sus tres tramos. */
export function rangoTotal(t: TiemposProveedor): Tramo {
  return {
    min: t.fabricacion.min + t.transito.min + t.nacionalizacion.min,
    max: t.fabricacion.max + t.transito.max + t.nacionalizacion.max,
  };
}

/**
 * El lead time EFECTIVO: el rango total colapsado a un número por el punto elegido.
 *
 * Se interpola sobre el rango TOTAL y no tramo por tramo, que da lo mismo por ser una
 * combinación lineal, pero se lee mucho mejor: "el proveedor tarda entre 110 y 155 días, y
 * estamos parados en el 50 % → 132".
 */
export function leadTimeEfectivo(t: TiemposProveedor): number {
  const { min, max } = rangoTotal(t);
  const punto = Math.min(100, Math.max(0, t.puntoRango));
  return Math.round(min + ((max - min) * punto) / 100);
}

/** Cómo describir el punto elegido en castellano, para la pantalla. */
export function etiquetaPunto(punto: number): string {
  if (punto <= 0) return 'el más optimista del rango';
  if (punto < 40) return 'cerca del mínimo';
  if (punto <= 60) return 'el promedio del rango';
  if (punto < 100) return 'cerca del máximo';
  return 'el más conservador del rango';
}
